// Turning a wall clock into an instant, and back, in a zone that is not
// necessarily this device's.
//
// Everything the app stores is an instant — `posts.scheduled_at` is timestamptz
// and the scheduler compares it to now(). What a person types is a wall clock:
// "the 9th at 09:00". The two are only the same thing once you name a zone, and
// until this file existed the zone was silently the browser's.
//
// There is no dependency here on purpose. The conversion needs one thing the
// platform already has — the tz database behind `Intl` — and a date library
// would be several hundred kilobytes to reach the same `Intl` call.

/** `YYYY-MM-DDTHH:mm`, the only format `<input type="datetime-local">` speaks. */
export type WallClock = string;

const PARTS = {
  year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", second: "2-digit",
} as const;

/**
 * Formatters are expensive to build and we build the same few over and over —
 * once per cell of a calendar month, in the worst case.
 */
const formatters = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(zone: string): Intl.DateTimeFormat {
  let f = formatters.get(zone);
  if (!f) {
    // `hourCycle: "h23"` rather than `hour12: false`: the latter still yields
    // "24" for midnight under some ICU versions, which parses an hour off.
    f = new Intl.DateTimeFormat("en-US", { timeZone: zone, hourCycle: "h23", ...PARTS });
    formatters.set(zone, f);
  }
  return f;
}

/** True when this engine's tz database knows the zone. */
export function isKnownZone(zone: string): boolean {
  try {
    partsFormatter(zone);
    return true;
  } catch {
    return false;
  }
}

/** The zone this device is set to, or UTC where the engine will not say. */
export function deviceZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * The clock reading in `zone` at `instant`, as a UTC timestamp.
 *
 * The trick the whole file rests on: format the instant in the target zone,
 * then read the resulting digits back as though they were UTC. The gap between
 * that number and the real instant IS the zone's offset at that moment.
 */
function clockAsUtc(instant: number, zone: string): number {
  const parts = partsFormatter(zone).formatToParts(new Date(instant));
  const at: Record<string, number> = {};
  for (const p of parts) if (p.type !== "literal") at[p.type] = Number(p.value);
  return Date.UTC(at.year, at.month - 1, at.day, at.hour, at.minute, at.second);
}

/** How far `zone` is ahead of UTC at `instant`, in milliseconds. */
export function offsetAt(zone: string, instant: number | Date): number {
  const ms = instant instanceof Date ? instant.getTime() : instant;
  return clockAsUtc(ms, zone) - ms;
}

/**
 * A wall clock in `zone` -> the instant it names.
 *
 * Solved by iteration rather than algebra, because the offset we need depends
 * on the instant we are trying to find. Reading the clock as if it were UTC
 * gives a first guess; the offset there is nearly always the right one, and a
 * second pass fixes the exception — a time within an hour or so of a DST
 * transition, where the first guess lands on the far side of it.
 *
 * Two boundary cases have no honest answer and get a documented one:
 *
 *   Spring forward. 02:30 on the day the clocks jump 02:00 -> 03:00 does not
 *   exist. We return the instant one hour later (03:30 local). Erring forwards
 *   matters more than erring backwards: a post scheduled into a gap should go
 *   out just after the hour it was aimed at, never an hour before it.
 *
 *   Fall back. 01:30 on the day the clocks drop 02:00 -> 01:00 happens twice.
 *   We return the first, which is what someone writing "01:30" means.
 */
export function wallClockToInstant(wall: WallClock, zone: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(wall.trim());
  if (!m) throw new Error(`not a wall clock: ${wall}`);
  const [, y, mo, d, h, mi, s] = m;
  const asUtc = Date.UTC(+y, +mo - 1, +d, +h, +mi, s ? +s : 0);

  const first = asUtc - offsetAt(zone, asUtc);
  const second = asUtc - offsetAt(zone, first);
  if (first === second) return new Date(first);

  // The two passes disagree, so a transition sits between them. Whichever
  // candidate actually reads back as the clock we were given is the real one;
  // if neither does we are in the spring-forward gap, and the later candidate
  // is the one that lands after it.
  if (clockAsUtc(second, zone) === asUtc) return new Date(second);
  if (clockAsUtc(first, zone) === asUtc) return new Date(first);
  return new Date(Math.max(first, second));
}

/** An instant -> the wall clock it reads as in `zone`, ready for an input. */
export function instantToWallClock(instant: Date | string | number, zone: string): WallClock {
  const ms = instant instanceof Date ? instant.getTime()
    : typeof instant === "string" ? new Date(instant).getTime()
    : instant;
  // Shifting by the offset and reading UTC fields gives the same digits the
  // zone would show, which is exactly what the input wants.
  return new Date(ms + offsetAt(zone, ms)).toISOString().slice(0, 16);
}

/**
 * `+05:30`, `-08:00`, `+00:00` — the offset as a person writes it.
 *
 * Minutes, not hours: Kolkata is half an hour off and Kathmandu three quarters,
 * and rounding either to a whole hour is how you miss a launch.
 */
export function offsetLabel(zone: string, at: Date = new Date()): string {
  const total = Math.round(offsetAt(zone, at) / 60_000);
  const sign = total < 0 ? "-" : "+";
  const abs = Math.abs(total);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

/**
 * The short name the zone is known by right now — `EDT`, `GMT+5:30`.
 *
 * Taken from the zone rather than assumed, so a zone on daylight time today
 * says so. Most of the world has no letter abbreviation and `Intl` hands back
 * an offset instead, which is the honest answer for those places.
 */
export function zoneAbbreviation(zone: string, at: Date = new Date()): string {
  return namePart(zone, at, "short") ?? `UTC${offsetLabel(zone, at)}`;
}

/**
 * The name a person would use for the zone: `India Standard Time`, `Eastern
 * Time`, `Japan Standard Time`.
 *
 * This is the whole reason the picker can be country-first. Nobody looking for
 * their clock is looking for `Asia/Kolkata` — they are looking for India, and
 * `longGeneric` is where the browser keeps that name. Generic rather than
 * specific on purpose: it reads "Eastern Time" all year instead of flipping
 * between "Eastern Standard Time" and "Eastern Daylight Time" twice a year
 * under a setting the person has not touched.
 *
 * Where there is no name — `UTC`, the `Etc/*` zones — it falls back to the
 * city, so the caller never has to handle an empty string.
 */
export function zoneName(zone: string, at: Date = new Date()): string {
  const name = namePart(zone, at, "longGeneric");
  return name && !isOffsetShaped(name) ? name : zoneCity(zone);
}

function namePart(
  zone: string,
  at: Date,
  timeZoneName: Intl.DateTimeFormatOptions["timeZoneName"],
): string | null {
  try {
    const parts = new Intl.DateTimeFormat("en", { timeZone: zone, timeZoneName, hour: "numeric" })
      .formatToParts(at);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? null;
  } catch {
    return null;
  }
}

/** `GMT+5:30`, `UTC-08:00`, `GMT` — a name that is really just an offset. */
function isOffsetShaped(name: string): boolean {
  return /^(GMT|UTC)([+-]\d{1,2}(:\d{2})?)?$/.test(name);
}

/** `Asia/Kolkata` -> `Kolkata`, for a label that has to fit on one line. */
export function zoneCity(zone: string): string {
  const tail = zone.split("/").pop() ?? zone;
  return tail.replace(/_/g, " ");
}

/**
 * The full thing a picker shows: `India Standard Time — Kolkata (UTC+05:30)`.
 *
 * The city is in there because the name alone is ambiguous for anyone choosing
 * between two zones in one country — `Eastern Time` and `Central Time` both
 * read as "American", and "Louisville" is what tells them apart. It is dropped
 * where it would only repeat the name.
 */
export function zoneLabel(zone: string, at: Date = new Date()): string {
  const name = zoneName(zone, at);
  const city = zoneCity(zone);
  const offset = `UTC${offsetLabel(zone, at)}`;
  return name === city ? `${name} (${offset})` : `${name} — ${city} (${offset})`;
}

/** An instant, written out in `zone` and said to be in it. */
export function formatInZone(
  instant: Date | string | number,
  zone: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" },
): string {
  const date = instant instanceof Date ? instant : new Date(instant);
  try {
    return new Intl.DateTimeFormat(undefined, { ...options, timeZone: zone }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

/**
 * The same, with the zone named after it, for anywhere a second person on the
 * workspace might read the time and assume it is theirs.
 */
export function formatInZoneWithName(instant: Date | string | number, zone: string): string {
  const date = instant instanceof Date ? instant : new Date(instant);
  return `${formatInZone(date, zone)} ${zoneAbbreviation(zone, date)}`;
}
